/**
 * Campaign Rules Tab — shared shell for every campaign type.
 *
 * Owns the common chrome (wrapper, content card, terms modal trigger, loader)
 * and delegates the campaign-specific sections to the `Content` component
 * passed in by each variant's RulesTab.
 */

import { memo, useCallback, useState, type ComponentType, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { testIds } from 'core/testIds';
import { useCampaignTypeContext, useSelectedCampaign, type CampaignSource } from 'features/campaigns';
import Image from 'shared/components/Image';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import CampaignTermsInfoModal from './CampaignTermsInfoModal/CampaignTermsInfoModal';

import styles from './css/RulesTab.module.css';

export interface IRulesContentProps {
  campaign: CampaignSource;
}

interface CampaignRulesTabProps {
  Content: ComponentType<IRulesContentProps>;
  termsFallback?: string;
}

const RulesTab: FC<CampaignRulesTabProps> = ({ Content, termsFallback }) => {
  const campaignType = useCampaignTypeContext();
  const campaign = useSelectedCampaign(campaignType);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const { t } = useTranslation();

  const handleTermsOpen = useCallback(() => setIsTermsOpen(true), []);
  const handleTermsClose = useCallback(() => setIsTermsOpen(false), []);

  if (!campaign) {
    return (
      <div className={styles.rulesWrapper}>
        <div className={styles.loaderContainer}>
          <LoadingIndicator show absolute />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.rulesWrapper}>
      {isTermsOpen && (
        <CampaignTermsInfoModal
          title={t('tournaments.terms_and_conditions', 'Terms & Conditions')}
          content={campaign.terms || termsFallback}
          onClose={handleTermsClose}
        />
      )}
      <div className={styles.contentContainerWrapper}>
        <div className={styles.contentContainer} data-testid={testIds.campaigns.rulesContent(campaignType)}>
          <Image
            onClick={handleTermsOpen}
            src="/campaigns/icons/Info"
            manualAffix=".svg"
            alt="Campaign Terms and Conditions"
            className={styles.infoIcon}
          />
          <div className={styles.textSection}>
            <Content campaign={campaign} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(RulesTab);
